<template>
  <div class="as-coach-checklist-inspection-table">
    <div class="as-coach-checklist-inspection-table__toolbar">
      <div
        class="as-coach-checklist-inspection-table__filter"
        role="radiogroup"
        aria-label="Checkpoint filter"
      >
        <button
          v-for="option in filterOptions"
          :key="option.value"
          type="button"
          class="as-coach-checklist-inspection-table__filter-option"
          :class="{
            'as-coach-checklist-inspection-table__filter-option--active':
              resolvedPassFilter === option.value,
          }"
          role="radio"
          :aria-checked="String(resolvedPassFilter === option.value)"
          @click="onPassFilterChange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="as-coach-checklist-inspection-table__toolbar-end">
        <div class="as-coach-checklist-inspection-table__toolbar-extra">
          <slot name="toolbar-extra" />
        </div>
        <div
          class="as-coach-checklist-inspection-table__search"
          role="search"
          aria-label="Search Criteria"
        >
          <ExTextInput
            :value="resolvedSearchQuery"
            ghost-text="Search Criteria"
            icon="interface_search"
            label=""
            hide-floating-label
            custom-height="40px"
            @input="onSearchInput"
          />
        </div>
      </div>
    </div>

    <div class="as-coach-checklist-inspection-table__groups">
      <section
        v-for="row in displayLevels"
        :key="row.key"
        class="as-coach-checklist-inspection-table__group"
      >
        <div
          class="as-coach-checklist-inspection-table__group-row"
          :class="{
            'as-coach-checklist-inspection-table__group-row--inline-badge': row.isInlineBadgeRow,
          }"
        >
          <button
            type="button"
            class="as-coach-checklist-inspection-table__group-header"
            :class="{
              'as-coach-checklist-inspection-table__group-header--inline-badge':
                row.isInlineBadgeRow,
              'as-coach-checklist-inspection-table__group-header--static': !row.showChevron,
            }"
            :aria-expanded="row.showChevron ? String(row.expanded) : undefined"
            :aria-label="`${row.level} checkpoints`"
            @click="onToggleLevel(row)"
          >
            <span
              class="as-coach-checklist-inspection-table__chevron"
              :class="{
                'as-coach-checklist-inspection-table__chevron--hidden': !row.showChevron,
              }"
              aria-hidden="true"
            >
              {{ row.expanded ? "▼" : "▶" }}
            </span>

            <InspectionLevelBadge :level="row.level" />

            <span class="as-coach-checklist-inspection-table__summary">
              {{ row.summary }}
            </span>

            <span
              v-if="row.showProgress"
              class="as-coach-checklist-inspection-table__progress"
              role="progressbar"
              :aria-valuenow="row.progressPercent"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-label="`${row.level} progress`"
            >
              <span
                class="as-coach-checklist-inspection-table__progress-fill"
                :class="row.progressFillClass"
                :style="{ '--progress-pct': String(row.progressPercent) }"
              />
            </span>
          </button>

          <ReadinessBadge
            v-if="row.awardedBadge"
            :key="row.awardedBadgeKey"
            v-bind="getAwardedBadgeProps(row)"
            @removeBadge="onAwardedBadgeRemove(row.group)"
            @expiration-synced="onAwardedBadgeExpirationSynced"
            @chatter-click="onAwardedBadgeChatter(row.group)"
          />

          <IssueBadgeButton
            v-if="row.showBadgeButton"
            :action="row.badgeAction"
            :tooltip-text="row.badgeTooltip"
            button-type="primary"
            size="small"
            class="as-coach-checklist-inspection-table__badge-btn"
            @onClick="onBadgeClick(row.group)"
          />
        </div>

        <ul
          v-if="row.expanded && row.hasCheckpoints"
          class="as-coach-checklist-inspection-table__cards"
        >
          <li
            v-for="checkpoint in row.checkpoints"
            :key="checkpointKey(checkpoint)"
            class="as-coach-checklist-inspection-table__card-item"
          >
            <AsACoachCheckpointInspectionCard
              :v2="true"
              :type="checkpoint.type"
              :status="checkpoint.status"
              :title="checkpoint.title"
              :status-chips="checkpoint.statusChips || []"
              :action-label="checkpoint.actionLabel || ''"
              :secondary-action-label="checkpoint.secondaryActionLabel || ''"
              :inspection-id="checkpoint.id"
              :employee-name="employeeName"
              :microlearning-completed-on="checkpoint.microlearningCompletedOn"
              :quiz-attempted-on="checkpoint.quizAttemptedOn"
              :inspected-by="checkpoint.inspectedBy"
              :inspected-on="checkpoint.inspectedOn"
              @action-click="onCheckpointAction(row.group, checkpoint, $event)"
              @secondary-action-click="onCheckpointSecondaryAction(row.group, checkpoint, $event)"
              @chatter-click="onCheckpointChatter(row.group, checkpoint)"
            />
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import AsACoachCheckpointInspectionCard from "@components/AsACoachCheckpointInspectionCard/AsACoachCheckpointInspectionCard.vue";
import InspectionLevelBadge from "@components/InspectionLevelBadge/InspectionLevelBadge.vue";
import IssueBadgeButton from "@components/IssueBadgeButton/IssueBadgeButton.vue";
import ReadinessBadge from "@components/ReadinessBadge/ReadinessBadge.vue";
import { ISSUE_BADGE_ACTIONS } from "@components/IssueBadgeButton/issueBadgeButtonConstants.js";

const filterOptions = [
  { value: "not-yet-passed", label: "Not yet passed" },
  { value: "all", label: "All" },
];

const DEFAULT_BADGE_TOOLTIPS = {
  Award: "Award a badge to the employee",
  Remove: "Remove the badge from the employee",
  "Inspect and Award": "Click to open Inspection Form",
};

const CHECKPOINT_STATE_BADGE_TOOLTIPS = {
  "Give Badge": {
    noCheckpoints: "This responsibility has no checklist, so a badge can be given directly.",
    incomplete: "All checkpoints must be passed before a badge can be given.",
  },
  "Upgrade Badge": {
    noCheckpoints:
      "This responsibility has no checklist, so the badge can be upgraded directly.",
    incomplete: "All checkpoints must be passed before the badge can be upgraded.",
  },
};

const props = defineProps({
  /**
   * Level groups for the accordion.
   * Shape: `{ id?, level, passed, total, expanded, emptyLabel?, badgeAction?, badgeTooltip?, checkpoints[] }`
   * Checkpoint shape matches AsACoachCheckpointInspectionCard v2 props (+ optional `id`).
   */
  levels: {
    type: Array,
    default: () => [],
    validator: (groups) =>
      groups.every(
        (group) =>
          group &&
          typeof group.level === "string" &&
          ["Apprentice", "Professional", "Proficient", "Coach", "Master", "Architect"].includes(group.level) &&
          (group.badgeAction == null ||
            group.badgeAction === "" ||
            ISSUE_BADGE_ACTIONS.includes(group.badgeAction))
      ),
  },
  /** Active pass filter: `not-yet-passed` | `all`. */
  passFilter: {
    type: String,
    default: "not-yet-passed",
    validator: (value) => ["not-yet-passed", "all"].includes(value),
  },
  /** Controlled search query. */
  searchQuery: {
    type: String,
    default: "",
  },
  /** Employee's own display name — forwarded to the badge tooltips (ER-11856). */
  employeeName: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:passFilter",
  "update:searchQuery",
  "pass-filter-change",
  "search",
  "toggle-level",
  "badge-click",
  "badge-remove",
  "badge-chatter",
  "expiration-synced",
  "action-click",
  "secondary-action-click",
  "chatter-click",
]);

const resolvedPassFilter = computed(() => props.passFilter || "not-yet-passed");
const resolvedSearchQuery = computed(() => props.searchQuery || "");

function checkpointKey(checkpoint) {
  return checkpoint.id || `${checkpoint.type}-${checkpoint.title}-${checkpoint.status}`;
}

function groupSummary(group) {
  const total = Number(group.total) || 0;
  if (total <= 0) {
    return group.emptyLabel || "No checkpoints required";
  }
  const passed = Number(group.passed) || 0;
  return `${passed} of ${total} checkpoints passed`;
}

function progressPercentFor(group) {
  const total = Number(group.total) || 0;
  if (total <= 0) return 0;
  const passed = Number(group.passed) || 0;
  return Math.max(0, Math.min(100, Math.round((passed / total) * 100)));
}

function progressFillClassFor(group) {
  const total = Number(group.total) || 0;
  const passed = Number(group.passed) || 0;
  if (total > 0 && passed >= total) {
    return "as-coach-checklist-inspection-table__progress-fill--complete";
  }
  return "as-coach-checklist-inspection-table__progress-fill--in-progress";
}

function badgeTooltipFor(group) {
  if (group.badgeTooltip) return group.badgeTooltip;

  const stateTooltips = CHECKPOINT_STATE_BADGE_TOOLTIPS[group.badgeAction];
  if (stateTooltips) {
    const total = Number(group.total) || 0;
    return total <= 0 ? stateTooltips.noCheckpoints : stateTooltips.incomplete;
  }

  return DEFAULT_BADGE_TOOLTIPS[group.badgeAction] || "Badge action";
}

function toDateOrNull(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function getAwardedBadgeProps(row) {
  const badge = row.awardedBadge;
  return {
    badgeId: badge.id,
    id: badge.id,
    level: badge.level,
    dot: badge.dot,
    view: "Editable",
    expiration_date: row.awardedBadgeExpirationDate,
    expires_in_days_from_becoming_obsolete: badge.expires_in_days_from_becoming_obsolete,
    has_expiration_if_obsolete: badge.has_expiration_if_obsolete,
    retries_to_expire_in_days: badge.retries_to_expire_in_days,
  };
}

const displayLevels = computed(() =>
  (props.levels || []).map((group) => {
    const total = Number(group.total) || 0;
    const checkpoints = Array.isArray(group.checkpoints) ? group.checkpoints : [];
    const badgeAction = group.badgeAction || null;
    const awardedBadge = group.awardedBadge || null;
    const hasCheckpoints = checkpoints.length > 0;
    return {
      group,
      key: group.id || group.level,
      level: group.level,
      expanded: hasCheckpoints && !!group.expanded,
      summary: groupSummary(group),
      showProgress: total > 0,
      progressPercent: progressPercentFor(group),
      progressFillClass: progressFillClassFor(group),
      hasCheckpoints,
      showChevron: hasCheckpoints,
      checkpoints,
      awardedBadge,
      awardedBadgeKey: `${group.level}-${awardedBadge?.id ?? "none"}-${awardedBadge?.dot ?? ""}`,
      awardedBadgeExpirationDate: toDateOrNull(awardedBadge?.expiration_date),
      showBadgeButton: !!badgeAction,
      isInlineBadgeRow: total <= 0 && (!!badgeAction || !!awardedBadge),
      badgeAction,
      badgeTooltip: badgeTooltipFor(group),
    };
  })
);

function onPassFilterChange(value) {
  if (value === resolvedPassFilter.value) return;
  emit("update:passFilter", value);
  emit("pass-filter-change", value);
}

function onSearchInput(value) {
  emit("update:searchQuery", value);
  emit("search", value);
}

function onToggleLevel(row) {
  if (!row.showChevron) return;
  emit("toggle-level", {
    id: row.group.id,
    level: row.group.level,
    expanded: !row.group.expanded,
  });
}

function onBadgeClick(group) {
  emit("badge-click", {
    id: group.id,
    level: group.level,
    badgeAction: group.badgeAction,
    group,
  });
}

function onAwardedBadgeRemove(group) {
  emit("badge-remove", {
    id: group.id,
    level: group.level,
    badge: group.awardedBadge,
    group,
  });
}

function onAwardedBadgeExpirationSynced() {
  emit("expiration-synced");
}

function onAwardedBadgeChatter(group) {
  emit("badge-chatter", {
    id: group.id,
    level: group.level,
    badge: group.awardedBadge,
    group,
  });
}

function onCheckpointAction(group, checkpoint, payload) {
  emit("action-click", { group, checkpoint, ...payload });
}

function onCheckpointSecondaryAction(group, checkpoint, payload) {
  emit("secondary-action-click", { group, checkpoint, ...payload });
}

function onCheckpointChatter(group, checkpoint) {
  emit("chatter-click", { group, checkpoint });
}
</script>

<style scoped>
.as-coach-checklist-inspection-table {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    "Segoe UI",
    Roboto,
    Arial,
    sans-serif;
}

.as-coach-checklist-inspection-table__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
}

.as-coach-checklist-inspection-table__filter {
  display: inline-flex;
  flex-shrink: 0;
  gap: 4px;
  padding: 3px;
  background: #f2f4f7;
  border-radius: 9999px;
}

.as-coach-checklist-inspection-table__filter-option {
  appearance: none;
  border: 1px solid transparent;
  background: transparent;
  padding: 5px 14px;
  border-radius: 9999px;
  color: #667085;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  white-space: nowrap;
  cursor: pointer;
}

.as-coach-checklist-inspection-table__filter-option--active {
  background: #ffffff;
  border-color: #eaecf0;
  color: #101828;
}

.as-coach-checklist-inspection-table__filter-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.as-coach-checklist-inspection-table__toolbar-end {
  display: flex;
  flex: 1 1 0;
  gap: 16px;
  align-items: center;
  min-width: 0;
}

.as-coach-checklist-inspection-table__toolbar-extra {
  flex: 1 1 0;
  min-width: 0;
}

.as-coach-checklist-inspection-table__search {
  flex: 1 1 0;
  max-width: 320px;
  min-width: 0;
}

.as-coach-checklist-inspection-table__search :deep(.ex_text_input) {
  width: 100%;
}

.as-coach-checklist-inspection-table__search :deep(.main_label) {
  display: none;
}

.as-coach-checklist-inspection-table__search :deep(.v-text-field__details) {
  display: none;
}

.as-coach-checklist-inspection-table__groups {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  min-width: 0;
}

.as-coach-checklist-inspection-table__group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  min-width: 0;
}

.as-coach-checklist-inspection-table__group-row {
  display: flex;
  gap: 14px;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.as-coach-checklist-inspection-table__group-row--inline-badge {
  gap: 10px;
}

.as-coach-checklist-inspection-table__group-header {
  display: flex;
  flex: 1 1 0;
  gap: 10px;
  align-items: center;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  box-sizing: border-box;
}

.as-coach-checklist-inspection-table__group-header--inline-badge {
  flex: 0 1 auto;
  width: auto;
}

.as-coach-checklist-inspection-table__group-header--static {
  cursor: default;
}

.as-coach-checklist-inspection-table__group-header:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 4px;
}

.as-coach-checklist-inspection-table__chevron {
  flex-shrink: 0;
  color: #667085;
  font-size: 11px;
  font-weight: 600;
  line-height: normal;
}

.as-coach-checklist-inspection-table__chevron--hidden {
  visibility: hidden;
}

.as-coach-checklist-inspection-table__summary {
  flex-shrink: 0;
  color: #475467;
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

.as-coach-checklist-inspection-table__progress {
  position: relative;
  flex: 1 1 0;
  min-width: 48px;
  height: 6px;
  overflow: hidden;
  background: #eaecf0;
  border-radius: 9999px;
}

.as-coach-checklist-inspection-table__progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(var(--progress-pct, 0) * 1%);
  height: 6px;
  border-radius: 9999px;
}

.as-coach-checklist-inspection-table__progress-fill--in-progress {
  background: #7f56d9;
}

.as-coach-checklist-inspection-table__progress-fill--complete {
  background: #17b26a;
}

.as-coach-checklist-inspection-table__badge-btn {
  flex-shrink: 0;
}

.as-coach-checklist-inspection-table__cards {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.as-coach-checklist-inspection-table__card-item {
  display: flex;
  align-self: stretch;
  width: 100%;
  min-width: 0;
}

.as-coach-checklist-inspection-table__card-item :deep(.as-a-coach-checkpoint-inspection-card) {
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
</style>
